<template>
    <section id="precios" class="w-full py-16 md:py-24 lg:py-32 bg-gradient-to-b from-eblack-950 via-eblack-900 to-eblack-950 relative flex items-center justify-center overflow-hidden font-modulus">

        <!-- Background accents -->
        <div class="absolute inset-0 bg-gradient-to-br from-eorange-500/5 to-transparent"> </div>
        <div class="absolute top-1/2 right-0 w-80 h-80 bg-eblue-500/5 rounded-full blur-3xl pointer-events-none"></div>

        <div class="container px-4 md:px-6 relative z-10">
            <!-- Header -->
            <div class="text-center mb-10">
                <h2 class="text-3xl md:text-5xl font-bold font-modulus-semi-bold tracking-tighter text-white mb-4">
                    {{ $t('plans_section.title') }}
                </h2>
                <p class="mx-auto max-w-[700px] text-ewhite-300 md:text-lg leading-relaxed">
                    {{ $t('plans_section.subtitle') }}
                </p>
            </div>

            <!-- Billing Toggle Switch -->
            <div class="flex items-center justify-center gap-3 mb-12">
                <span :class="['text-sm font-semibold transition-colors duration-200', !isAnnual ? 'text-white' : 'text-ewhite-400']">
                    {{ $t('plans_section.monthly') }}
                </span>
                <button
                    type="button"
                    @click="isAnnual = !isAnnual"
                    class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none bg-ewhite-800"
                    :class="isAnnual ? 'bg-eorange-500' : 'bg-ewhite-700'"
                >
                    <span
                        class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out"
                        :class="isAnnual ? 'translate-x-5' : 'translate-x-0'"
                    ></span>
                </button>
                <span :class="['text-sm font-semibold transition-colors duration-200 flex items-center gap-1.5', isAnnual ? 'text-white' : 'text-ewhite-400']">
                    {{ $t('plans_section.yearly') }}
                    <span class="px-2 py-0.5 text-[10px] font-bold bg-eorange-500/20 text-eorange-400 border border-eorange-500/30 rounded-full">
                        {{ $t('plans_section.save_20') }}
                    </span>
                </span>
            </div>

            <!-- Pricing Grid -->
            <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-4 max-w-7xl mx-auto">
                <div
                    class="flex flex-col gap-6 rounded-2xl border p-6 shadow-lg transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl group relative"
                    :class="{
                        'bg-ewhite-50/[0.03] border-ewhite-50/10 hover:bg-ewhite-50/[0.06] hover:border-ewhite-50/20': !plan.popular,
                        'bg-gradient-to-br from-eorange-500/20 to-eorange-500/5 border-eorange-500/50 hover:border-eorange-500 hover:shadow-eorange-500/10': plan.popular
                    }"
                    v-for="plan in pricingPlans" :key="plan.id"
                >
                    <!-- Popular Badge -->
                    <div class="absolute -top-3.5 left-1/2 transform -translate-x-1/2" v-if="plan.popular">
                        <span class="bg-eorange-500 text-white px-4 py-1 text-xs font-bold rounded-full font-modulus-semi-bold shadow-md shadow-eorange-500/25">
                            {{ $t('plans_section.more_popular') }}
                        </span>
                    </div>

                    <div class="text-center pb-6 border-b border-ewhite-50/10 flex flex-col items-center">
                        <div
                            class="bg-ewhite-50/5 w-14 h-14 rounded-full flex items-center justify-center mb-4 group-hover:bg-eorange-500/20 transition-all duration-300"
                        >
                            <IconUsers
                                stroke-width="2"
                                class="h-6 w-6 text-white group-hover:text-eorange-500 transition-colors duration-300"
                            />
                        </div>
                        <h3 class="text-xl font-bold text-white mb-2 font-modulus-semi-bold">{{ $t(plan.title) }}</h3>

                        <!-- Price section -->
                        <div class="space-y-1 my-3">
                            <div class="text-3xl font-extrabold text-white tracking-tight font-modulus-semi-bold">
                                {{ getPlanPrice(plan) }}
                            </div>
                            <span v-if="isAnnual && (plan.id === 2 || plan.id === 3)" class="text-[10px] text-eorange-400 block font-semibold">
                                {{ $t('plans_section.billed_annually') }}
                            </span>
                        </div>

                        <p class="text-xs text-ewhite-300 font-modulus leading-relaxed h-12 flex items-center justify-center">
                            {{ $t(plan.description) }}
                        </p>
                    </div>

                    <!-- Features List -->
                    <div class="space-y-3 py-2 flex-grow">
                        <div class="flex items-start space-x-2.5" v-for="(feature, index) in plan.features" :key="index">
                            <IconCircleCheck
                                stroke-width="2"
                                class="h-4 w-4 text-eorange-500 flex-shrink-0 mt-0.5"
                            />
                            <span class="text-xs text-ewhite-200 font-modulus leading-tight">{{ $t(feature) }}</span>
                        </div>
                    </div>

                    <!-- Call To Action -->
                    <div class="pt-4 mt-auto">
                        <button
                            type="button"
                            @click="scrollToCTA"
                            :class="[
                                'px-4 py-2.5 rounded-full w-full font-bold text-xs transition-all duration-300',
                                plan.popular
                                    ? 'bg-eorange-500 text-white hover:bg-eorange-600 shadow-md shadow-eorange-500/20 hover:shadow-lg'
                                    : 'bg-ewhite-50/10 hover:bg-ewhite-50/20 text-white border border-ewhite-50/10 hover:border-eorange-500/50'
                            ]"
                        >
                            {{ $t('plans_section.start_free') }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Footer note -->
            <div class="text-center mt-12">
                <p class="text-ewhite-400 text-xs font-semibold">
                    {{ $t('plans_section.footer_note') }}
                </p>
            </div>
        </div>
    </section>
</template>

<script>
import { IconCircleCheck, IconUsers } from '@tabler/icons-vue';

export default {
    name: 'PricingSection',
    data() {
        return {
            isAnnual: false,
            pricingPlans: [
                {
                    id: 1,
                    title: 'plans_section.plans.plan_1.title',
                    description: 'plans_section.plans.plan_1.description',
                    features: [
                        'plans_section.plans.plan_1.feature_1',
                        'plans_section.plans.plan_1.feature_2',
                        'plans_section.plans.plan_1.feature_3'
                    ],
                    popular: false
                },
                {
                    id: 2,
                    title: 'plans_section.plans.plan_2.title',
                    description: 'plans_section.plans.plan_2.description',
                    features: [
                        'plans_section.plans.plan_2.feature_1',
                        'plans_section.plans.plan_2.feature_2',
                        'plans_section.plans.plan_2.feature_3'
                    ],
                    popular: true
                },
                {
                    id: 3,
                    title: 'plans_section.plans.plan_3.title',
                    description: 'plans_section.plans.plan_3.description',
                    features: [
                        'plans_section.plans.plan_3.feature_1',
                        'plans_section.plans.plan_3.feature_2',
                        'plans_section.plans.plan_3.feature_3'
                    ],
                    popular: false
                },
                {
                    id: 4,
                    title: 'plans_section.plans.plan_4.title',
                    description: 'plans_section.plans.plan_4.description',
                    features: [
                        'plans_section.plans.plan_4.feature_1',
                        'plans_section.plans.plan_4.feature_2',
                        'plans_section.plans.plan_4.feature_3'
                    ],
                    popular: false
                },
            ]
        }
    },
    methods: {
        getPlanPrice(plan) {
            if (plan.id === 1) return this.$t('plans_section.plans.plan_1.price');
            if (plan.id === 4) return this.$t('plans_section.plans.plan_4.price');

            const monthlyPrice = plan.id === 2 ? 80 : 95;
            const billingLabel = this.$t('plans_section.monthly').toLowerCase();

            if (this.isAnnual) {
                const annualRate = Math.floor(monthlyPrice * 0.8);
                return `$${annualRate} + Taxes / ${billingLabel}`;
            } else {
                return `$${monthlyPrice} + Taxes / ${billingLabel}`;
            }
        },
        scrollToCTA() {
            const ctaSection = document.getElementById('cta-section');
            if (ctaSection) {
                ctaSection.scrollIntoView({ behavior: 'smooth' });
            }
        }
    },
    components: {
        IconCircleCheck,
        IconUsers
    }
}
</script>

<style lang="scss" scoped></style>